import _isEqual from 'lodash/isEqual';
import { v4 as uuidv4 } from 'uuid';

import type { Business } from 'app-components/flux/business/businessTypes';
import { mkFilter } from 'app-components/flux/filters/filtersTypes';
import {
  type ComparisonRangeInput,
  DEFAULT_CHART_VIEW,
  DEFAULT_DELTA_DISPLAY_MODE,
  type DisplayOptionsType,
  mkReportConfigFilters,
  mkReportConfiguration,
  type ReportConfiguration,
} from 'app-components/flux/report-configurations/reportConfigurationsTypes';
import { normalizeGeneratedReportRange } from 'app-components/utils/date';

import { DeductionType } from '../mileage-tracker/mileageTrackerDeductionForm';
import { resolveDeltaDisplayMode } from './utils/comparisonModes';
import { cloneComparisonRanges } from './utils/comparisonRanges';
import { extractUsageFromFilter, getReportFieldConfig } from './utils/reportFieldAvailability';

export const baseDisplayOptions: DisplayOptionsType = {
  alternateRows: false,
  deltaDisplayMode: DEFAULT_DELTA_DISPLAY_MODE,
  density: 'COMPACT',
  hideCents: false,
  showExpensesAsNegative: false,
  showOtherOnTop: false,
  showSubTotalsOnTop: false,
  showPLGraph: true,
  showExcludedTxns: false,
};

const DEFAULT_SOURCE = 'SAVED_REPORTS';
const SPENDING_DASHBOARD = 'SPENDING_DASHBOARD';

const normalizeFilterId = (id: any): any => (typeof id === 'string' ? id.trim().toLowerCase() : id);

const normalizeComparisonRangeAliases = (ranges: readonly ComparisonRangeInput[]): ComparisonRangeInput[] =>
  ranges.map((range) => ({
    ...range,
    dateRange: normalizeGeneratedReportRange(range.dateRange) ?? range.dateRange,
  }));

// Default configurations - empty filter items, populated at runtime based on user data
export const defaultReportConfigurations: Record<string, ReportConfiguration> = {
  'total-spending': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'total-spending',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'TRANSACTION',
    reportSubType: 'SPENDING',
    persistentType: 'TEMPORARY',
    dateRange: 'MONTH_TO_DATE',
    rowType: 'CATEGORY',
    columnType: 'NONE',
    timestep: 'NONE',
    chartType: 'PIE',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
    options: {
      viewToShow: 'GRAPH_AND_GRID',
      hideReportHeader: false,
    },
  }),

  income: mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'income',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'TRANSACTION',
    reportSubType: 'INCOME',
    persistentType: 'TEMPORARY',
    dateRange: 'MONTH_TO_DATE',
    rowType: 'CATEGORY',
    columnType: 'NONE',
    timestep: 'NONE',
    chartType: 'PIE',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),

  'net-income': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'net-income',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_6_MONTHS',
    rowType: 'CATEGORY',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),

  'profit-loss': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'profit-loss',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_6_MONTHS',
    rowType: 'CATEGORY',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),

  'income-expense': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'income-expense',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_6_MONTHS',
    rowType: 'CATEGORY',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),

  savings: mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'savings',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'SAVINGS',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_6_MONTHS',
    rowType: 'ACCOUNT',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'MULTILINE',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),

  'net-worth': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'net-worth',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_WORTH',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_6_MONTHS',
    rowType: 'ACCOUNT',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'MULTILINE',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions, startAtZero: false },
  }),

  'net-worth-page': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'net-worth-page',
    source: 'NET_WORTH',
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_WORTH',
    persistentType: 'TEMPORARY',
    dateRange: 'SAME_DAY_LAST_6_MONTHS',
    rowType: 'ACCOUNT',
    columnType: 'TIME',
    timestep: 'DAY',
    chartType: 'MULTILINE',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions, startAtZero: false, showExcludedAccounts: true },
  }),

  taxes: mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'taxes',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    deductionType: DeductionType.ACTUAL,
    reportType: 'TRANSACTION',
    reportSubType: 'TAX_SUMMARY',
    persistentType: 'TEMPORARY',
    dateRange: 'YEAR_TO_DATE',
    rowType: 'USAGE',
    columnType: 'NONE',
    chartType: 'NONE',
    timestep: 'YEAR',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    options: {
      viewToShow: 'GRID',
      hideReportHeader: true,
    },
    displayOptions: { ...baseDisplayOptions, showPLGraph: false },
  }),

  'balance-sheet': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'balance-sheet',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'BALANCE_SHEET',
    persistentType: 'TEMPORARY',
    dateRange: 'YEAR_TO_DATE',
    rowType: 'BUSINESS',
    columnType: 'TIME',
    timestep: 'YEAR',
    chartType: 'NONE',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    options: {
      viewToShow: 'GRAPH_AND_GRID',
      showInterval: false,
      hideReportHeader: true,
    },
    displayOptions: { ...baseDisplayOptions, showPLGraph: false },
  }),
  custom: mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'custom',
    source: DEFAULT_SOURCE,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_6_MONTHS',
    rowType: 'CATEGORY',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),

  // ## -   Spending Dashboard report configs    - ##
  'spending-dashboard': mkReportConfiguration({
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    name: 'spending-dashboard',
    source: SPENDING_DASHBOARD,
    accountingType: 'CASH',
    reportType: 'SUMMARY',
    reportSubType: 'NET_INCOME',
    persistentType: 'TEMPORARY',
    dateRange: 'RECENT_12_MONTHS',
    rowType: 'CATEGORY',
    columnType: 'TIME',
    timestep: 'MONTH',
    chartType: 'NET_INCOME',
    filter: mkReportConfigFilters({
      type: 'REPORT',
      filterItems: [],
    }),
    displayOptions: { ...baseDisplayOptions },
  }),
};

// Personal taxes configuration - uses different report structure
export const personalTaxesConfiguration: ReportConfiguration = mkReportConfiguration({
  id: undefined,
  clientId: uuidv4().toUpperCase(),
  name: 'taxes',
  source: DEFAULT_SOURCE,
  accountingType: 'CASH',
  reportType: 'TRANSACTION',
  reportSubType: 'TAX_SUMMARY',
  persistentType: 'TEMPORARY',
  dateRange: 'YEAR_TO_DATE',
  rowType: 'USAGE',
  columnType: 'NONE',
  chartType: 'NONE',
  filter: mkReportConfigFilters({
    type: 'REPORT',
    filterItems: [
      {
        type: 'USAGE',
        usages: [{ id: '0', type: 'PERSONAL' }],
      },
    ],
  }),
  options: {
    viewToShow: 'GRID',
    hideReportHeader: true,
  },
  displayOptions: { ...baseDisplayOptions, showPLGraph: false },
});

// QCS will replace this in a week or two, this is a temporary addition
export const netWorthPageNewDefaultConfig: ReportConfiguration = mkReportConfiguration({
  id: undefined,
  clientId: uuidv4().toUpperCase(),
  name: 'net-worth-page',
  source: 'NET_WORTH',
  accountingType: 'CASH',
  reportType: 'SUMMARY',
  reportSubType: 'NET_WORTH',
  persistentType: 'TEMPORARY',
  dateRange: 'SAME_DAY_LAST_6_MONTHS',
  rowType: 'ACCOUNT',
  columnType: 'TIME',
  timestep: 'DAY',
  chartType: 'MULTILINE',
  filter: mkReportConfigFilters({
    type: 'REPORT',
    filterItems: [],
  }),
  displayOptions: { ...baseDisplayOptions, startAtZero: true, showExcludedAccounts: true },
});

// Helper function to get configuration for a specific tab
export const getReportConfiguration = (
  tabName: string,
  isPersonalTaxes: boolean = false,
  startYAxisAtZero: undefined | boolean = false,
): ReportConfiguration | undefined => {
  // Special case: personal taxes uses different configuration
  if (tabName === 'taxes' && isPersonalTaxes) {
    return personalTaxesConfiguration;
  }
  if (tabName === 'net-worth-page' && startYAxisAtZero) {
    return netWorthPageNewDefaultConfig;
  }

  return defaultReportConfigurations[tabName];
};

/**
 * Clone a report configuration with new unique IDs
 * This creates a fresh copy of any report configuration with new unique IDs
 *
 * @param sourceConfig - The source configuration to clone
 * @param overrides - Optional properties to override in the cloned configuration
 * @returns A new ReportConfiguration with fresh IDs
 */
export const cloneReportConfiguration = (
  sourceConfig: ReportConfiguration,
  overrides?: Partial<Parameters<typeof mkReportConfiguration>[0]>,
): ReportConfiguration => {
  // Create a new configuration with fresh IDs and any overrides
  return mkReportConfiguration({
    // Copy all properties from the source config
    name: sourceConfig.name,
    source: sourceConfig.source ?? DEFAULT_SOURCE,
    accountingType: sourceConfig.accountingType,
    reportType: sourceConfig.reportType,
    reportSubType: sourceConfig.reportSubType,
    persistentType: sourceConfig.persistentType,
    dateRange: sourceConfig.dateRange,
    startOn: sourceConfig.startOn,
    endOn: sourceConfig.endOn,
    dateRangeInterval: sourceConfig.dateRangeInterval,
    dateRangeIntervalCount: sourceConfig.dateRangeIntervalCount,
    rowType: sourceConfig.rowType,
    columnType: sourceConfig.columnType,
    timestep: sourceConfig.timestep,
    chartType: sourceConfig.chartType,
    options: sourceConfig.options,
    displayOptions: sourceConfig.displayOptions,
    description: sourceConfig.description,
    // Deep clone so the copy never shares mutable range objects with the source config.
    comparisonRanges: cloneComparisonRanges(sourceConfig.comparisonRanges),
    primaryAdditionalColumnTypes: sourceConfig.primaryAdditionalColumnTypes
      ? [...sourceConfig.primaryAdditionalColumnTypes]
      : undefined,
    showSubTotalLabel: sourceConfig.showSubTotalLabel,
    showSubTotalsOnBottom: sourceConfig.showSubTotalsOnBottom,
    showTotalRows: sourceConfig.showTotalRows,
    isDeleted: sourceConfig.isDeleted,
    modifiedAt: sourceConfig.modifiedAt,
    location: sourceConfig.location,
    deductionType: sourceConfig.deductionType,
    // Generate fresh IDs
    id: undefined,
    clientId: uuidv4().toUpperCase(),
    // Deep clone the filter to ensure we don't share filter instances
    filter: sourceConfig.filter
      ? mkReportConfigFilters({
          type: sourceConfig.filter.type,
          filterItems: sourceConfig.filter.filterItems ? [...sourceConfig.filter.filterItems] : undefined,
          parentIds: sourceConfig.filter.parentIds,
          clientId: sourceConfig.filter.clientId,
          isDeleted: sourceConfig.filter.isDeleted,
          // id: uuidv4() // Give the filter a new ID too
        })
      : undefined,
    // Apply any overrides last
    ...overrides,
  });
};

/**
 * Clone a default report configuration with new unique IDs
 * This is a convenience function that gets a default configuration by tab name
 * and then clones it with fresh IDs.
 *
 * @param tabName - The tab name to clone the configuration for
 * @param isPersonalTaxes - Whether to use the personal taxes configuration (only applies to 'taxes' tab)
 * @param overrides - Optional properties to override in the cloned configuration
 * @returns A new ReportConfiguration with fresh IDs, or undefined if tab not found
 */
export const cloneDefaultReportConfiguration = (
  tabName: string,
  isPersonalTaxes: boolean = false,
  startYAxisAtZero: boolean = false,
  overrides?: Partial<Parameters<typeof mkReportConfiguration>[0]>,
): ReportConfiguration | undefined => {
  const defaultConfig = getReportConfiguration(tabName, isPersonalTaxes, startYAxisAtZero);

  if (!defaultConfig) {
    return undefined;
  }

  return cloneReportConfiguration(defaultConfig, overrides);
};

/**
 * Check whether an OPEN config has been modified from its parent.
 * Works with both PREDEFINED parents (from defaultsMap) and SAVED/CUSTOM parents (from configsMap).
 */
export const isConfigModifiedFromParent = (
  config: ReportConfiguration,
  configsMap: { get(key: string): ReportConfiguration | undefined },
  defaultsMap: { get(key: string): ReportConfiguration | undefined },
): boolean => {
  if ((config.persistentType !== 'OPEN' && config.persistentType !== 'TEMPORARY') || !config.parentConfigId)
    return false;
  const parent =
    config.parentPersistentType === 'PREDEFINED'
      ? defaultsMap.get(config.parentConfigId)
      : configsMap.get(config.parentConfigId);
  if (!parent || parent.isDeleted) return false;

  const excluded = config.reportType === 'TRANSACTION' ? ['name', 'timestep'] : ['name'];

  return !compareReportConfigs(config, parent, excluded);
};

// All available tab names
export const availableTabNames = [
  'total-spending',
  'income',
  'income-expense',
  'net-income',
  'profit-loss',
  'savings',
  'net-worth',
  'taxes',
  'balance-sheet',
] as const;

/**
 * Helper function to create default usage filter based on report field configuration
 * This centralizes the logic used by both useDefaultReportConfig hook and isDefaultReportConfiguration
 */
export const createDefaultUsageFilter = (
  tabName: string,
  businessEnabled: boolean,
  startYAxisAtZero: undefined | boolean,
  openBusinesses: Business[],
  advancedReportingEnabled: boolean,
  reportTaxDetailBusiness: boolean,
  reportTaxDetailPersonal: boolean,
  reportGridGraph: boolean,
) => {
  const defaultReportConfig = getReportConfiguration(tabName, !businessEnabled, startYAxisAtZero);
  if (!defaultReportConfig) {
    return undefined;
  }
  const reportFieldConfig = getReportFieldConfig(
    defaultReportConfig as ReportConfiguration,
    businessEnabled,
    advancedReportingEnabled,
    reportTaxDetailBusiness,
    openBusinesses,
    reportTaxDetailPersonal,
    reportGridGraph,
  );

  const { defaultUsageOptions } = reportFieldConfig || {};
  let filter = undefined;

  if (businessEnabled && defaultUsageOptions && defaultUsageOptions.length > 0) {
    const initialUsageArray = defaultUsageOptions.map((b: any) => ({
      id: b.value === '0' ? '0' : (b.id ?? ''),
      type: b.value === '0' ? 'PERSONAL' : 'BUSINESS',
    }));

    // Only add usage filter if there are usage options
    const filterItems = [];
    if (initialUsageArray.length > 0) {
      filterItems.push({ type: 'USAGE' as const, usages: initialUsageArray });
    }

    filter = mkFilter({
      type: 'REPORT',
      filterItems,
    });
  }

  return filter ?? defaultReportConfig.filter;
};

/**
 * Compare two report configurations field-by-field.
 * Excludes specified fields, treats null/undefined as equivalent,
 * accepts legacy date range aliases, and does order-independent USAGE filter matching.
 */
export const compareReportConfigs = (current: any, expected: any, excludeFields: string[] = []): boolean => {
  const fieldsToCompare = [
    'name',
    'accountingType',
    'reportType',
    'reportSubType',
    'dateRange',
    'startOn',
    'endOn',
    'dateRangeInterval',
    'dateRangeIntervalCount',
    'rowType',
    'columnType',
    'timestep',
    'chartType',
    'filter',
    'displayOptions',
    'comparisonRanges',
    'primaryAdditionalColumnTypes',
  ].filter((field) => !excludeFields.includes(field));

  for (const field of fieldsToCompare) {
    if (field === 'filter') {
      const currentFilter = current.filter;
      const expectedFilter = expected.filter;
      const ignoreHiddenUsage = current.reportSubType === 'SAVINGS' || current.reportSubType === 'NET_WORTH';
      const currentItems = (currentFilter?.filterItems || []).filter(
        (item: { type: string }) => !ignoreHiddenUsage || item.type !== 'USAGE',
      );
      const expectedItems = (expectedFilter?.filterItems || []).filter(
        (item: { type: string }) => !ignoreHiddenUsage || item.type !== 'USAGE',
      );

      if (!currentFilter && !expectedFilter) continue;
      if (!currentFilter || !expectedFilter) {
        if (ignoreHiddenUsage && currentItems.length === 0 && expectedItems.length === 0) continue;
        return false;
      }
      if (currentFilter.type !== expectedFilter.type) return false;

      if (currentItems.length !== expectedItems.length) return false;

      // Compare each filter item by type — match by type then compare the item's data arrays by ID sets
      for (const currentItem of currentItems) {
        const expectedItem = expectedItems.find((item: any) => item.type === currentItem.type);
        if (!expectedItem) return false;

        // Get the data array for this filter type (usages, accounts, tags, payees, chartOfAccounts)
        const dataKeyMap: Record<string, string> = {
          USAGE: 'usages',
          ACCOUNT: 'accounts',
          TAG: 'tags',
          PAYEE: 'payees',
          CHARTOFACCOUNT: 'chartOfAccounts',
        };
        const dataKey = dataKeyMap[currentItem.type] || null;

        if (!dataKey) continue;

        const currentArr = currentItem[dataKey] || [];
        const expectedArr = expectedItem[dataKey] || [];

        if (currentArr.length !== expectedArr.length) return false;

        // Build ID sets.
        const getId = (item: any) => {
          const id = typeof item === 'string' ? item : item.id;
          return normalizeFilterId(id);
        };
        const expectedIds = new Set(expectedArr.map(getId));
        for (const item of currentArr) {
          if (!expectedIds.has(getId(item))) return false;
        }
      }

      for (const expectedItem of expectedItems) {
        if (!currentItems.find((item: any) => item.type === expectedItem.type)) return false;
      }
    } else if (field === 'displayOptions') {
      const currentOpts = current.displayOptions || {};
      const expectedOpts = expected.displayOptions || {};
      const keys = new Set([...Object.keys(currentOpts), ...Object.keys(expectedOpts)]);
      for (const key of keys) {
        const currentValue = currentOpts[key];
        const expectedValue = expectedOpts[key];
        if (key === 'startAtZero' && (currentValue ?? false) === (expectedValue ?? false)) continue;
        if (
          key === 'deltaDisplayMode' &&
          resolveDeltaDisplayMode(currentOpts) === resolveDeltaDisplayMode(expectedOpts)
        )
          continue;
        if (key === 'chartView' && (currentValue ?? DEFAULT_CHART_VIEW) === (expectedValue ?? DEFAULT_CHART_VIEW))
          continue;
        if (key === 'columns' && _isEqual(currentValue ?? [], expectedValue ?? [])) continue;

        const isCurrentNullish = currentValue === null || currentValue === undefined;
        const isExpectedNullish = expectedValue === null || expectedValue === undefined;

        if (isCurrentNullish && isExpectedNullish) continue;
        if (currentValue !== expectedValue) return false;
      }
    } else if (field === 'comparisonRanges' || field === 'primaryAdditionalColumnTypes') {
      // Deep AND order-sensitive: range order determines generated column order, and
      // `additionalColumnTypes` order determines column order within a range. A reorder is a
      // real edit, so an order-insensitive comparison here would make Save / Reset /
      // "unsaved changes" silently ignore it. Missing and empty are equivalent.
      const currentArr =
        field === 'comparisonRanges' ? normalizeComparisonRangeAliases(current[field] ?? []) : (current[field] ?? []);
      const expectedArr =
        field === 'comparisonRanges' ? normalizeComparisonRangeAliases(expected[field] ?? []) : (expected[field] ?? []);

      // `_isEqual` is order-sensitive for arrays but order-insensitive for object keys, so a
      // server-loaded range and a UI-built one with the same values still compare equal.
      if (currentArr.length !== expectedArr.length) return false;
      if (currentArr.length > 0 && !_isEqual(currentArr, expectedArr)) return false;
    } else {
      // Direct comparison for primitive fields, treating null and undefined as equivalent
      // Generated snapshots and saved configurations can carry different IDs for the same
      // recent preset. Treat only known aliases as equivalent; preserve unknown values.
      const currentValue =
        field === 'dateRange' ? (normalizeGeneratedReportRange(current[field]) ?? current[field]) : current[field];
      const expectedValue =
        field === 'dateRange' ? (normalizeGeneratedReportRange(expected[field]) ?? expected[field]) : expected[field];

      // Explicit bounds are ignored when generating a predefined date range. QCS can retain
      // or normalize them after a custom range is reset, so they only affect synchronization
      // while either configuration uses a range that supports the corresponding bound.
      if (
        (field === 'startOn' &&
          !['CUSTOM', 'CUSTOM_SINCE'].includes(current.dateRange) &&
          !['CUSTOM', 'CUSTOM_SINCE'].includes(expected.dateRange)) ||
        (field === 'endOn' && current.dateRange !== 'CUSTOM' && expected.dateRange !== 'CUSTOM') ||
        ((field === 'dateRangeInterval' || field === 'dateRangeIntervalCount') &&
          !['CUSTOM_RECENT', 'CUSTOM_LAST'].includes(current.dateRange) &&
          !['CUSTOM_RECENT', 'CUSTOM_LAST'].includes(expected.dateRange))
      ) {
        continue;
      }

      // Treat null and undefined as equivalent
      const isCurrentNullish = currentValue === null || currentValue === undefined;
      const isExpectedNullish = expectedValue === null || expectedValue === undefined;

      if (isCurrentNullish && isExpectedNullish) {
        // Both are null/undefined, consider them equal
        continue;
      } else if (currentValue !== expectedValue) {
        return false;
      }
    }
  }

  return true;
};

/**
 * Check if a report configuration matches its default configuration
 * Excludes id and clientId from comparison, but includes filter with default business selection
 */
export const isDefaultReportConfiguration = (
  currentConfig: ReportConfiguration,
  tabName: string,
  businessEnabled: boolean,
  startYAxisAtZero: boolean | undefined,
  usageConfig: any,
): boolean => {
  const usageFromConfig = extractUsageFromFilter(currentConfig) || [];
  const hasBusinesses = (usageConfig?.usageOptions ?? []).some((option: any) => option?.value && option.value !== '0');
  // Personal taxes = PERSONAL usage only AND no businesses exist (personal-only dataset).
  const isPersonalTaxes = tabName === 'taxes' && usageFromConfig.includes('0') && !hasBusinesses;
  const defaultConfig = getReportConfiguration(tabName, isPersonalTaxes || !businessEnabled, startYAxisAtZero);
  if (!defaultConfig) {
    return false;
  }

  const { defaultUsageOptions } = usageConfig || {};
  let defaultUsageArray: Array<{ id: string; type: 'PERSONAL' | 'BUSINESS' }> = [];
  if (isPersonalTaxes) {
    defaultUsageArray = [{ id: '0', type: 'PERSONAL' }];
  } else if (businessEnabled && defaultUsageOptions) {
    defaultUsageArray = defaultUsageOptions.map((b: any) => ({
      id: b.value === '0' ? '0' : (b.id ?? ''),
      type: b.value === '0' ? 'PERSONAL' : 'BUSINESS',
    }));
  }

  // Only add usage filter if there are usage options
  const filterItems = [];
  if (defaultUsageArray.length > 0) {
    filterItems.push({ type: 'USAGE' as const, usages: defaultUsageArray });
  }

  const defaultFilter = mkReportConfigFilters({
    type: 'REPORT',
    filterItems,
  });

  // Create expected default config with proper filter
  // Convert Immutable Record to plain object first, then add filter
  const expectedDefaultWithFilter = {
    ...defaultConfig.toJS(),
    filter: defaultFilter,
  };

  const excluded = currentConfig.reportType === 'TRANSACTION' ? ['id', 'clientId', 'timestep'] : ['id', 'clientId'];

  return compareReportConfigs(currentConfig, expectedDefaultWithFilter, excluded);
};
